import { FC, useState } from 'react';
import { Box, IconButton, Stack, TableCell as MuiTableCell, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { TableCell } from './TableCell';
import { getWidget } from './widgets/widgetDefinitions';
import { Phase } from '../JourneyDetailPage';
import { Widget } from '../../../gql/graphql';
import { DocumentType, getFragmentData, graphql } from '../../../gql';
import { DeleteOutline, Edit } from '@mui/icons-material';
import { MIN_NUMBER_OF_ACTIVITIES } from './DetailTable';
import { Draggable } from 'react-beautiful-dnd';

export const journeyDetail_widgets = graphql(`
	fragment journeyDetail_widgets on Activity {
		id
		links
		description
		notes
		goalsAndObjectives
		personas {
			id
			name
			...PersonaAvatar_persona
		}
		duration {
			type
			start
			id
			end
			occurence
			unit
			showOnActivityMap
		}
		satisfaction
		tags {
			id
			name
		}
		controlCycle {
			days
			history {
				at
			}
		}
		owner {
			...OwnerCell_Member
		}
		contentMetrics {
			id
			metric {
				id
				name
				unitSign
				dataSet {
					metricTemplate {
						unitSign
					}
				}
			}
		}
		pictures {
			id
			order
			image {
				id
				url(width: 150)
			}
		}
		...PictureGrid_Gallery
		opportunities(filter: { status: { notEq: resolved } }) {
			id
			name
			description
			status
		}
		actions(filter: { status: { notEq: resolved } }) {
			id
			name
			description
			status
			deadline
		}

		subTouchpoints {
			id
			type
			activity {
				name
				id
			}
		}
		touchpoint {
			parentActivity {
				name
			}
		}
		connectedJourneys {
			id
			name
			color
		}
	}
`);

export type WidgetsData = DocumentType<typeof journeyDetail_widgets>;

type Props = Readonly<{
	phases: readonly Phase[];
	handleDelete: () => void;
	handleEditName: () => void;
	journeyWidget: Widget;
	isFirst?: boolean;
	isLast?: boolean;
	index: number;
}>;

export const WidgetRow: FC<Props> = ({ phases, handleDelete, handleEditName, journeyWidget, isFirst, isLast, index }) => {
	const theme = useTheme();

	const intl = useIntl();
	const activities = phases.flatMap((phase) => phase.touchpoints.map(({ activity }) => getFragmentData(journeyDetail_widgets, activity)));
	const [showEdit, setShowEdit] = useState(false);

	return (
		<Draggable draggableId={journeyWidget.id} index={index}>
			{(provided) => (
				<tr ref={provided.innerRef} {...provided.draggableProps}>
					<TableCell
						key={journeyWidget.type}
						header
						isSticky
						data-widget-type={journeyWidget.type}
						onMouseEnter={() => setShowEdit(true)}
						onMouseLeave={() => setShowEdit(false)}
						{...provided.dragHandleProps}
					>
						<Box
							className={'widgets-handle'}
							sx={{
								'&:hover': { cursor: 'grab' },
								'&:active': { cursor: 'grabbing' },
							}}
						>
							<Stack direction={'row'} justifyContent={'flex-end'}>
								{/* NOTE: This condition restrict edit mode just for "notes" widget. Remove this to enable it for the rest of widgets  */}
								{journeyWidget.type === 'notes' && (
									<Tooltip title={intl.formatMessage({ id: 'journey.detail.widget.edit' })}>
										<IconButton
											onClick={() => handleEditName()}
											sx={{ position: 'absolute', top: 4, right: 30, width: '20px', height: '20px' }}
											color={'default'}
										>
											<Edit sx={{ width: '20px', height: '20px', opacity: showEdit ? 1 : 0 }} />
										</IconButton>
									</Tooltip>
								)}
								<Tooltip title={intl.formatMessage({ id: 'journey.detail.widget.delete' })}>
									<IconButton
										onClick={() => handleDelete()}
										sx={{ position: 'absolute', top: 4, right: 4, width: '20px', height: '20px' }}
										color={'default'}
									>
										<DeleteOutline sx={{ width: '20px', height: '20px', opacity: showEdit ? 1 : 0 }} />
									</IconButton>
								</Tooltip>
							</Stack>
							<Typography>{journeyWidget.name || getWidget(journeyWidget.type).name}</Typography>
						</Box>
					</TableCell>
					{phases.map((phase, index) => {
						const isLastPhase = index === phases.length - 1;
						if (phase.touchpoints.length === 0 && isFirst) {
							return (
								<TableCell
									key={phase.id}
									sx={{
										position: 'relative',
										borderRight: isLastPhase ? undefined : `2px solid transparent`,
									}}
									rowSpan={0}
								></TableCell>
							);
						}

						if (phase.touchpoints.length === 0 && isFirst === false) {
							return null;
						}

						const emptyCellsCount: number = MIN_NUMBER_OF_ACTIVITIES - phase.touchpoints.length;
						const emptyCells = Array.from({ length: emptyCellsCount }, () => null);
						return (
							<td
								key={phase.id}
								colSpan={phase.touchpoints.length}
								style={{
									padding: 0,
								}}
							>
								<table
									style={{
										position: 'relative',
										width: '100%',
										height: '100%',
										border: 'none',
										borderSpacing: 0,
										tableLayout: 'fixed',
									}}
								>
									<tbody>
										<tr>
											{phase.touchpoints.map(({ activity }, index) => {
												if (!activity) {
													return null;
												}

												const WidgetComponent = getWidget(journeyWidget.type).component;
												const widget = getFragmentData(journeyDetail_widgets, activity);
												return (
													<WidgetComponent
														expand={emptyCellsCount > 0 && index === phase.touchpoints.length - 1 ? emptyCellsCount + 1 : 0}
														activities={activities}
														key={widget.id}
														activityId={widget.id}
														widget={widget}
														activityName={activity.name}
													/>
												);
											})}
											{journeyWidget.type !== 'satisfaction' &&
												emptyCells.map((_, index) => (
													<MuiTableCell
														key={index}
														sx={{
															minWidth: 290,
															fontSize: 14,
															borderBottom: isLast ? `0.5px solid ${theme.palette.grey[200]}` : 'none',
														}}
													/>
												))}
										</tr>
									</tbody>
								</table>
							</td>
						);
					})}
				</tr>
			)}
		</Draggable>
	);
};
